import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { PageHeader } from './ui/PageHeader';
import { Card, CardBody } from './ui/Card';
import { Button } from './ui/Button';
import { SegmentedControl } from './ui/SegmentedControl';
import { ExportMenu } from './ui/ExportMenu';
import { SkeletonCard } from './ui/Skeleton';
import { EmptyState } from './ui/EmptyState';
import DatePicker from './DatePicker';
import {
  DayHistoryTable,
  DayConsolidatedTable,
  buildDayHistoryRows,
  buildDayConsolidatedRows,
  getConsolidateGroup,
} from './DayHistoryTable';
import { MonthlyReportTable } from './MonthlyReportTable';
import { NutritionReportPanel } from './NutritionReportPanel';
import { toDateIso, normalizeDateIso } from '../utils/dateIso';
import { getIstDateIso } from '../utils/istTime';
import { isHighlightedClass } from '../utils/classHighlight';

export function TeacherHistoryPanel() {
  const app = useApp();
  const {
    labels,
    useMalayalam,
    teacherClasses,
    teacherSchoolClasses,
    selectedDate,
    setSelectedDate,
    selectedMonth,
    setSelectedMonth,
    historicalStrengths,
    monthlyReport,
    nutritionHistoricalDates,
    nutritionHistoricalReport,
    selectedNutritionDate,
    setSelectedNutritionDate,
    isLoading,
    fetchTeacherSchoolClasses,
    fetchTeacherHistoricalReport,
    fetchTeacherMonthlyReport,
    fetchTeacherNutritionHistoricalDates,
    fetchTeacherNutritionHistoricalReport,
    exportDayHistoryData,
    exportMonthlyReport,
    exportNutritionData,
    exportNutritionAll,
  } = app;

  const highlightClassIds = useMemo(
    () => (teacherClasses || []).map((c) => c.id).filter(Boolean),
    [teacherClasses]
  );

  const [historyMode, setHistoryMode] = useState('day');
  const [historyDataMode, setHistoryDataMode] = useState('nm');
  const [dayExportOpen, setDayExportOpen] = useState(false);
  const [monthlyExportOpen, setMonthlyExportOpen] = useState(false);

  const dayHistoryDateIso = selectedDate ? toDateIso(selectedDate) : '';

  const nutritionAvailableDates = useMemo(() => {
    const set = new Set(
      (nutritionHistoricalDates || []).map((d) => normalizeDateIso(d)).filter(Boolean)
    );
    const reportDate = normalizeDateIso(nutritionHistoricalReport?.date);
    if (reportDate) set.add(reportDate);
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [nutritionHistoricalDates, nutritionHistoricalReport?.date]);

  useEffect(() => {
    void fetchTeacherSchoolClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (historyDataMode !== 'nutrition') return;
    void fetchTeacherNutritionHistoricalDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyDataMode]);

  useEffect(() => {
    if (historyDataMode !== 'nutrition') return;
    const today = getIstDateIso();
    setSelectedNutritionDate((prev) => {
      const p = normalizeDateIso(prev);
      if (p && nutritionAvailableDates.includes(p)) return p;
      if (nutritionAvailableDates.includes(today)) return today;
      return nutritionAvailableDates[0] || today;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyDataMode, nutritionAvailableDates]);

  useEffect(() => {
    if (historyDataMode !== 'nutrition' || !selectedNutritionDate) return;
    void fetchTeacherNutritionHistoricalReport(normalizeDateIso(selectedNutritionDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyDataMode, selectedNutritionDate]);

  useEffect(() => {
    if (historyDataMode !== 'nm') return;
    if (historyMode === 'day' || historyMode === 'dayConsolidated') {
      if (selectedDate) {
        void fetchTeacherHistoricalReport(selectedDate);
      }
    } else if (selectedMonth) {
      void fetchTeacherMonthlyReport(selectedMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyDataMode, historyMode, selectedDate, selectedMonth]);

  const dayHistoryRows = useMemo(
    () => buildDayHistoryRows(teacherSchoolClasses, historicalStrengths, dayHistoryDateIso),
    [teacherSchoolClasses, historicalStrengths, dayHistoryDateIso]
  );
  const dayConsolidatedRows = useMemo(
    () => buildDayConsolidatedRows(dayHistoryRows),
    [dayHistoryRows]
  );
  const highlightGroups = useMemo(() => {
    const groups = new Set();
    dayHistoryRows.forEach((row) => {
      if (isHighlightedClass(row.class_id, highlightClassIds)) {
        groups.add(getConsolidateGroup(row.division_name));
      }
    });
    return Array.from(groups);
  }, [dayHistoryRows, highlightClassIds]);

  const myClassNames = (teacherClasses || []).map((c) => c.division_name).filter(Boolean).join(', ');

  return (
    <div className="space-y-4">
      <PageHeader
        title={labels.historicalReportsTitle}
        subtitle={myClassNames || undefined}
      />

      {highlightClassIds.length > 0 && (
        <p className="rounded-lg border border-primary/20 bg-primary-light/50 px-3 py-2 text-sm text-primary">
          {labels.teacherHistoryHighlightHint}
        </p>
      )}

      <SegmentedControl
        options={[
          { value: 'nm', label: labels.historyModeNm },
          { value: 'nutrition', label: labels.historyModeNutrition },
        ]}
        value={historyDataMode}
        onChange={setHistoryDataMode}
      />

      {historyDataMode === 'nutrition' ? (
        <NutritionReportPanel
          labels={labels}
          report={nutritionHistoricalReport}
          isLoading={isLoading.nutritionHistorical}
          selectedDate={selectedNutritionDate}
          onDateChange={setSelectedNutritionDate}
          availableDates={nutritionAvailableDates}
          exportNutritionData={exportNutritionData}
          exportNutritionAll={exportNutritionAll}
          showDelete={false}
          highlightClassIds={highlightClassIds}
        />
      ) : (
        <>
          <Card className="overflow-visible">
            <CardBody className="space-y-4 overflow-visible">
              <SegmentedControl
                options={[
                  { value: 'day', label: labels.reportModeDay },
                  { value: 'dayConsolidated', label: labels.reportModeDayConsolidated },
                  { value: 'month', label: labels.reportModeMonth },
                ]}
                value={historyMode}
                onChange={(mode) => {
                  setHistoryMode(mode);
                  setMonthlyExportOpen(false);
                }}
              />
              {historyMode === 'day' || historyMode === 'dayConsolidated' ? (
                <div className="space-y-3">
                  <label className="mb-1.5 block text-sm font-medium">{labels.selectDatePrompt}</label>
                  <DatePicker selectedDate={selectedDate} onDateChange={setSelectedDate} />
                  <div className="relative z-20 flex flex-wrap items-center gap-2">
                    <ExportMenu
                      open={dayExportOpen}
                      onOpenChange={setDayExportOpen}
                      labels={labels}
                      align="left"
                      placement="up"
                      disabled={!selectedDate || teacherSchoolClasses.length === 0}
                      onExport={(format) =>
                        exportDayHistoryData({
                          rows: historyMode === 'dayConsolidated' ? dayConsolidatedRows : dayHistoryRows,
                          fileType: format,
                          dateIso: dayHistoryDateIso,
                          isConsolidated: historyMode === 'dayConsolidated',
                        })
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">{labels.selectMonth}</label>
                    <input
                      type="month"
                      className="w-full min-h-touch rounded-lg border border-border bg-input px-3 py-2.5 text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                  </div>
                  <div className="relative z-20 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => fetchTeacherMonthlyReport(selectedMonth)}
                      isLoading={isLoading.monthlyReport}
                    >
                      {labels.viewReport}
                    </Button>
                    <ExportMenu
                      open={monthlyExportOpen}
                      onOpenChange={setMonthlyExportOpen}
                      labels={labels}
                      align="left"
                      placement="up"
                      disabled={!monthlyReport?.rows?.length || !monthlyReport?.dates?.length}
                      onExport={(format) => exportMonthlyReport(format)}
                    />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {historyMode === 'day' ? (
            isLoading.historicalReport ? (
              <SkeletonCard />
            ) : !selectedDate ? (
              <EmptyState title={labels.noData} message={labels.selectDatePrompt} />
            ) : teacherSchoolClasses.length === 0 ? (
              <EmptyState
                title={labels.noData}
                message={useMalayalam ? 'സ്കൂൾ ക്ലാസുകൾ ലോഡ് ചെയ്യുന്നു…' : 'Loading school classes…'}
              />
            ) : (
              <DayHistoryTable
                rows={dayHistoryRows}
                labels={labels}
                showDelete={false}
                highlightClassIds={highlightClassIds}
              />
            )
          ) : historyMode === 'dayConsolidated' ? (
            isLoading.historicalReport ? (
              <SkeletonCard />
            ) : !selectedDate ? (
              <EmptyState title={labels.noData} message={labels.selectDatePrompt} />
            ) : teacherSchoolClasses.length === 0 ? (
              <EmptyState title={labels.noData} message={labels.selectDatePrompt} />
            ) : (
              <DayConsolidatedTable
                rows={dayConsolidatedRows}
                labels={labels}
                highlightGroups={highlightGroups}
              />
            )
          ) : isLoading.monthlyReport ? (
            <SkeletonCard />
          ) : !monthlyReport ? (
            <EmptyState title={labels.noData} message={labels.selectMonth} />
          ) : monthlyReport.dates?.length === 0 ? (
            <EmptyState title={labels.noMonthlyData} message={labels.selectMonth} />
          ) : (
            <MonthlyReportTable
              report={monthlyReport}
              labels={labels}
              useMalayalam={useMalayalam}
              highlightClassIds={highlightClassIds}
            />
          )}
        </>
      )}
    </div>
  );
}
